import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ticketsService, pagosService, locatariosService } from '../services/api'
import { IconTicket, IconAlertTriangle, IconClockExclamation, IconCircleCheck, IconClock } from '@tabler/icons-react'

const NOMBRES_MES = { '01': 'Ene', '02': 'Feb', '03': 'Mar', '04': 'Abr', '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Ago', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dic' }

const formatoTiempo = (minutos) => {
  if (minutos === null || minutos === undefined) return 'N/A'
  if (minutos < 60) return Math.round(minutos) + ' min'
  const horas = minutos / 60
  if (horas < 24) return horas.toFixed(1) + ' h'
  return (horas / 24).toFixed(1) + ' dias'
}

export default function Dashboard() {
  const navigate = useNavigate()
  const [metricas, setMetricas] = useState(null)
  const [tickets, setTickets] = useState([])
  const [morosos, setMorosos] = useState([])
  const [totalResidentes, setTotalResidentes] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const cargar = async () => {
      try {
        const [metricasRes, ticketsRes, morososRes, locatariosRes] = await Promise.all([
          ticketsService.dashboardMetricas(),
          ticketsService.listar({ limit: 5 }),
          pagosService.morosos(),
          locatariosService.listar({ limit: 1 })
        ])
        setMetricas(metricasRes.data)
        setTickets(ticketsRes.data.data || [])
        setMorosos(morososRes.data.data?.slice(0, 4) || [])
        setTotalResidentes(locatariosRes.data.total || 0)
      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    cargar()
  }, [])

  const estadoColor = (estado) => {
    const map = { abierto: 'badge-abierto', en_proceso: 'badge-en_proceso', resuelto: 'badge-resuelto' }
    return map[estado] || 'badge-abierto'
  }

  if (loading || !metricas) return <div style={{ padding: 40, color: '#8A8A7A' }}>Cargando...</div>

  const maxMes = Math.max(1, ...metricas.resueltosPorMes.map(m => m.total))
  const totalCanal = metricas.porCanal.reduce((s, c) => s + c.total, 0) || 1

  return (
    <div>
      <div className="metrics-grid">
        <div className="metric">
          <div className="metric-label">Tickets abiertos</div>
          <div className={`metric-value ${metricas.ticketsAbiertos > 0 ? 'danger' : 'success'}`}>
            {metricas.ticketsAbiertos}
          </div>
          <div className="metric-sub">requieren atención</div>
        </div>
        <div className="metric" style={{ cursor: 'pointer' }} onClick={() => navigate('/tickets')}>
          <div className="metric-label">🚨 Sin atención 2+ días</div>
          <div className={`metric-value ${metricas.sinAtencion > 0 ? 'danger' : 'success'}`}>
            {metricas.sinAtencion}
          </div>
          <div className="metric-sub">ver en Tickets →</div>
        </div>
        <div className="metric">
          <div className="metric-label">Urgentes activos</div>
          <div className={`metric-value ${metricas.ticketsUrgentes > 0 ? 'danger' : 'success'}`}>
            {metricas.ticketsUrgentes}
          </div>
          <div className="metric-sub">ahora mismo</div>
        </div>
        <div className="metric">
          <div className="metric-label">Resueltos hoy</div>
          <div className="metric-value success">{metricas.resueltosHoy}</div>
          <div className="metric-sub">desde medianoche</div>
        </div>
        <div className="metric">
          <div className="metric-label">Residentes</div>
          <div className="metric-value gold">{totalResidentes}</div>
          <div className="metric-sub">en el sistema</div>
        </div>
        <div className="metric">
          <div className="metric-label">Con deuda vencida</div>
          <div className={`metric-value ${morosos.length > 0 ? 'danger' : 'success'}`}>
            {morosos.length}
          </div>
          <div className="metric-sub">residentes morosos</div>
        </div>
        <div className="metric">
          <div className="metric-label">Tiempo promedio de respuesta</div>
          <div className="metric-value gold">{formatoTiempo(metricas.tiempoPromedioMinutos)}</div>
          <div className="metric-sub">últimos 30 días ({metricas.conteoRespuestas} tickets)</div>
        </div>
        <div className="metric">
          <div className="metric-label">Tickets por canal</div>
          <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
            {metricas.porCanal.map(c => (
              <div key={c.canal}>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#8A6930' }}>{c.total}</div>
                <div className="metric-sub" style={{ textTransform: 'capitalize' }}>{c.canal} ({Math.round(c.total / totalCanal * 100)}%)</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">Tickets resueltos por mes</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, height: 140, padding: '10px 4px' }}>
          {metricas.resueltosPorMes.map(m => (
            <div key={m.mes} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{m.total}</div>
              <div style={{
                width: '100%',
                maxWidth: 44,
                height: Math.max(6, (m.total / maxMes) * 100) + 'px',
                background: '#8A6930',
                borderRadius: '4px 4px 0 0'
              }} />
              <div className="text-muted" style={{ marginTop: 6 }}>{NOMBRES_MES[m.mes.slice(5, 7)] || m.mes.slice(5, 7)}</div>
            </div>
          ))}
          {metricas.resueltosPorMes.length === 0 && <p className="text-muted">Sin datos de meses anteriores</p>}
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-title">
            Tickets recientes
            <span className="card-link" onClick={() => navigate('/tickets')}>Ver todos →</span>
          </div>
          {tickets.length === 0 && <p className="text-muted">No hay tickets registrados</p>}
          {tickets.map(t => (
            <div key={t.id} onClick={() => navigate('/tickets')}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid #E8E0D0', cursor: 'pointer' }}>
              <div className={`prio-dot prio-${t.prioridad}`} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {t.asunto}
                </div>
                <div className="text-muted" style={{ marginTop: 2 }}>
                  {t.locatario?.nombre} · {t.canal}
                </div>
              </div>
              <span className={`badge ${estadoColor(t.estado)}`}>{t.estado}</span>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="card-title">
            Cartera vencida
            <span className="card-link" onClick={() => navigate('/pagos')}>Ver todos →</span>
          </div>
          {morosos.length === 0 && <p className="text-muted">Sin deudas registradas</p>}
          {morosos.map((m, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #E8E0D0' }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: '#F5EDDD', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 500, color: '#8A6930', flexShrink: 0 }}>
                {m.locatario?.subscriptionExternalId?.slice(0, 2) || 'CA'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{m.locatario?.subscriptionExternalId}</div>
                <div className="text-muted">{m.locatario?.nombre}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#993C1D' }}>
                ${m.totalDeuda?.toLocaleString('es-MX')}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
